import { useState, useEffect, useCallback } from 'react';
import { 
  Shield, Users, AlertTriangle, RefreshCw, Copy, Eye, EyeOff, 
  ChevronDown, ChevronUp, LogOut, MapPin, Clock, Activity,
  Check, X, Bell, Settings, RotateCcw
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_KEY = 'inspiteof-admin-2024'; // In production, this should be entered by admin

// Severity badge colors
const severityColors = {
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  info: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
};

// Stats card component
const StatCard = ({ label, value, icon: Icon, trend, color = 'text-zinc-100' }) => (
  <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-zinc-500 text-xs uppercase tracking-wide">{label}</span>
      {Icon && <Icon className="w-4 h-4 text-zinc-600" />}
    </div>
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
    {trend && (
      <div className={`text-xs mt-1 ${trend > 0 ? 'text-emerald-400' : 'text-zinc-500'}`}>
        {trend > 0 ? '+' : ''}{trend}% vs yesterday
      </div>
    )}
  </div>
);

// Alert card component
const AlertCard = ({ alert, onAcknowledge }) => (
  <div className={`border rounded-lg p-4 ${alert.acknowledged ? 'bg-zinc-900/30 border-zinc-800/50' : 'bg-zinc-900/50 border-zinc-800'}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-3">
        <AlertTriangle className={`w-5 h-5 mt-0.5 ${alert.severity === 'critical' ? 'text-red-400' : alert.severity === 'high' ? 'text-orange-400' : 'text-amber-400'}`} />
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${severityColors[alert.severity] || severityColors.info}`}>
              {alert.severity?.toUpperCase()}
            </span>
            <span className="text-zinc-500 text-xs">{new Date(alert.created_at).toLocaleString()}</span>
          </div>
          <p className="text-zinc-200 text-sm">{alert.message}</p>
          {alert.data && (
            <p className="text-zinc-500 text-xs mt-1">
              {Object.entries(alert.data).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' | ')}
            </p>
          )}
        </div>
      </div>
      {!alert.acknowledged && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onAcknowledge(alert.id)}
          className="text-zinc-400 hover:text-zinc-200"
        >
          <Check className="w-4 h-4" />
        </Button>
      )}
    </div>
  </div>
);

// Session row component
const SessionRow = ({ session, onInvalidate }) => (
  <div className="flex items-center justify-between py-3 border-b border-zinc-800/50 last:border-0">
    <div className="flex items-center gap-4">
      <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center">
        <Users className="w-4 h-4 text-zinc-500" />
      </div>
      <div>
        <div className="flex items-center gap-2">
          <span className="text-zinc-300 text-sm font-mono">{session.ip_address}</span>
          {session.is_active && (
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-500 mt-0.5">
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {session.country || 'Unknown'}{session.city && session.city !== 'Unknown' ? `, ${session.city}` : ''}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {new Date(session.last_activity).toLocaleString()}
          </span>
        </div>
      </div>
    </div>
    {session.is_active && (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => onInvalidate(session.id)}
        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
      >
        <LogOut className="w-4 h-4" />
      </Button>
    )}
  </div>
);

export const PatreonAdminPanel = ({ onClose }) => {
  const [config, setConfig] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [expandedSection, setExpandedSection] = useState('overview');
  
  // Form states
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newNote, setNewNote] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [configRes, statsRes, sessionsRes, alertsRes] = await Promise.all([
        axios.get(`${API}/admin/patreon/config?secret_key=${ADMIN_KEY}`),
        axios.get(`${API}/admin/patreon/usage-stats?secret_key=${ADMIN_KEY}`),
        axios.get(`${API}/admin/patreon/sessions?secret_key=${ADMIN_KEY}`),
        axios.get(`${API}/admin/patreon/alerts?secret_key=${ADMIN_KEY}`),
      ]);
      
      setConfig(configRes.data);
      setUsageStats(statsRes.data);
      setSessions(sessionsRes.data.sessions || []);
      setAlerts(alertsRes.data.alerts || []);
      
      if (configRes.data.configured) {
        setNewUsername(configRes.data.username || '');
        setNewNote(configRes.data.note || '');
      }
    } catch (error) {
      console.error('Error fetching Patreon data:', error);
      toast.error('Failed to load Patreon data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateConfig = async (updates) => {
    setIsUpdating(true);
    try {
      await axios.put(`${API}/admin/patreon/config?secret_key=${ADMIN_KEY}`, updates);
      toast.success('Configuration updated');
      await fetchData();
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRotateCredentials = async () => {
    if (!window.confirm('This will generate new credentials and log out ALL active Patreon sessions. Continue?')) {
      return;
    }
    
    setIsUpdating(true);
    try {
      const response = await axios.post(`${API}/admin/patreon/rotate?secret_key=${ADMIN_KEY}`);
      toast.success(`Credentials rotated! New: ${response.data.new_username} / ${response.data.new_password}`);
      
      // Copy to clipboard
      navigator.clipboard.writeText(`Username: ${response.data.new_username}\nPassword: ${response.data.new_password}`);
      toast.info('New credentials copied to clipboard');
      
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rotation failed');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleInvalidateAllSessions = async () => {
    if (!window.confirm('This will log out ALL active Patreon sessions. Continue?')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/admin/patreon/sessions?secret_key=${ADMIN_KEY}`);
      toast.success(`${response.data.sessions_invalidated} sessions invalidated`);
      await fetchData();
    } catch (error) {
      toast.error('Failed to invalidate sessions');
    }
  };

  const handleInvalidateSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/admin/patreon/sessions/${sessionId}?secret_key=${ADMIN_KEY}`);
      toast.success('Session invalidated');
      await fetchData();
    } catch (error) {
      toast.error('Failed to invalidate session');
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await axios.post(`${API}/admin/patreon/alerts/${alertId}/acknowledge?secret_key=${ADMIN_KEY}`);
      setAlerts(alerts.map(a => a.id === alertId ? { ...a, acknowledged: true } : a));
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleAcknowledgeAllAlerts = async () => {
    try {
      await axios.post(`${API}/admin/patreon/alerts/acknowledge-all?secret_key=${ADMIN_KEY}`);
      setAlerts(alerts.map(a => ({ ...a, acknowledged: true })));
      toast.success('All alerts acknowledged');
    } catch (error) {
      toast.error('Failed to acknowledge alerts');
    }
  };

  const handleToggleEnabled = async () => {
    await handleUpdateConfig({ enabled: !config?.enabled });
  };

  const copyCredentials = () => {
    if (config?.username) {
      navigator.clipboard.writeText(`Username: ${config.username}`);
      toast.success('Username copied to clipboard');
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-8 text-center">
          <RefreshCw className="w-8 h-8 text-zinc-400 animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading Patreon admin...</p>
        </div>
      </div>
    );
  }

  const unacknowledgedAlerts = alerts.filter(a => !a.acknowledged);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-t-xl p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-zinc-100">Patreon Access Manager</h2>
                  <p className="text-zinc-500 text-sm">Manage shared access for Patreon members</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {unacknowledgedAlerts.length > 0 && (
                  <div className="flex items-center gap-1 bg-red-500/10 border border-red-500/30 rounded-full px-3 py-1">
                    <Bell className="w-4 h-4 text-red-400" />
                    <span className="text-red-400 text-sm font-medium">{unacknowledgedAlerts.length}</span>
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={fetchData}
                  className="text-zinc-400 hover:text-zinc-200"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="text-zinc-400 hover:text-zinc-200"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="bg-zinc-950 border-x border-zinc-800 p-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <StatCard 
                label="Active Sessions" 
                value={usageStats?.active_sessions || 0} 
                icon={Users}
                color={usageStats?.active_sessions > 50 ? 'text-amber-400' : 'text-zinc-100'}
              />
              <StatCard 
                label="Logins (24h)" 
                value={usageStats?.total_logins_24h || 0} 
                icon={Activity}
              />
              <StatCard 
                label="Unique IPs (24h)" 
                value={usageStats?.unique_ips_24h || 0} 
                icon={MapPin}
                color={usageStats?.unique_ips_24h > 20 ? 'text-amber-400' : 'text-zinc-100'}
              />
              <StatCard 
                label="Regions (24h)" 
                value={usageStats?.unique_regions_24h || 0} 
                icon={MapPin}
              />
            </div>
          </div>

          {/* Alerts Section */}
          {unacknowledgedAlerts.length > 0 && (
            <div className="bg-zinc-950 border-x border-zinc-800 p-6 border-t border-zinc-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-zinc-100 font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  Active Alerts
                </h3>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleAcknowledgeAllAlerts}
                  className="text-zinc-400 hover:text-zinc-200 text-xs"
                >
                  Acknowledge All
                </Button>
              </div>
              <div className="space-y-3">
                {unacknowledgedAlerts.map(alert => (
                  <AlertCard key={alert.id} alert={alert} onAcknowledge={handleAcknowledgeAlert} />
                ))}
              </div>
            </div>
          )}

          {/* Configuration Section */}
          <div className="bg-zinc-950 border-x border-zinc-800 p-6 border-t border-zinc-800">
            <button
              onClick={() => setExpandedSection(expandedSection === 'config' ? '' : 'config')}
              className="flex items-center justify-between w-full text-left"
            >
              <h3 className="text-zinc-100 font-semibold flex items-center gap-2">
                <Settings className="w-4 h-4 text-zinc-400" />
                Credentials & Settings
              </h3>
              {expandedSection === 'config' ? (
                <ChevronUp className="w-4 h-4 text-zinc-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-zinc-500" />
              )}
            </button>
            
            {expandedSection === 'config' && (
              <div className="mt-6 space-y-6">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg">
                  <div>
                    <p className="text-zinc-200 font-medium">Patreon Access</p>
                    <p className="text-zinc-500 text-sm">Enable or disable shared login</p>
                  </div>
                  <Switch
                    checked={config?.enabled || false}
                    onCheckedChange={handleToggleEnabled}
                    disabled={isUpdating}
                  />
                </div>

                {/* Current Credentials */}
                <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-zinc-400 text-sm">Current Username</p>
                    <div className="flex items-center gap-2">
                      <code className="text-zinc-200 bg-zinc-800 px-3 py-1 rounded font-mono text-sm">
                        {config?.username || 'Not set'}
                      </code>
                      <Button size="icon" variant="ghost" onClick={copyCredentials} className="h-8 w-8">
                        <Copy className="w-3 h-3 text-zinc-500" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-zinc-400 text-sm">Note</p>
                    <span className="text-zinc-300 text-sm">{config?.note || 'No note'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-zinc-400 text-sm">Version</p>
                    <span className="text-zinc-300 text-sm">v{config?.credential_version || 1}</span>
                  </div>
                </div>

                {/* Update Form */}
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-sm">New Username</Label>
                      <Input
                        value={newUsername}
                        onChange={(e) => setNewUsername(e.target.value)}
                        placeholder="patreon_user"
                        className="bg-zinc-900 border-zinc-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-sm">New Password</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Leave blank to keep current"
                          className="bg-zinc-900 border-zinc-700 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400 text-sm">Note (e.g., "April 2026 Access")</Label>
                    <Input
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Label this credential cycle"
                      className="bg-zinc-900 border-zinc-700"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleUpdateConfig({
                        username: newUsername || undefined,
                        password: newPassword || undefined,
                        note: newNote || undefined,
                      })}
                      disabled={isUpdating || (!newUsername && !newPassword && !newNote)}
                      className="bg-zinc-800 hover:bg-zinc-700"
                    >
                      Save Changes
                    </Button>
                    <Button
                      onClick={handleRotateCredentials}
                      disabled={isUpdating}
                      variant="outline"
                      className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Rotate & Reset All
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sessions Section */}
          <div className="bg-zinc-950 border-x border-zinc-800 p-6 border-t border-zinc-800">
            <button
              onClick={() => setExpandedSection(expandedSection === 'sessions' ? '' : 'sessions')}
              className="flex items-center justify-between w-full text-left"
            >
              <h3 className="text-zinc-100 font-semibold flex items-center gap-2">
                <Users className="w-4 h-4 text-zinc-400" />
                Active Sessions ({sessions.filter(s => s.is_active).length})
              </h3>
              {expandedSection === 'sessions' ? (
                <ChevronUp className="w-4 h-4 text-zinc-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-zinc-500" />
              )}
            </button>
            
            {expandedSection === 'sessions' && (
              <div className="mt-6">
                {sessions.length > 0 ? (
                  <>
                    <div className="flex justify-end mb-4">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleInvalidateAllSessions}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs"
                      >
                        <LogOut className="w-3 h-3 mr-1" />
                        Log Out All
                      </Button>
                    </div>
                    <div className="bg-zinc-900/30 border border-zinc-800 rounded-lg divide-y divide-zinc-800/50">
                      {sessions.map(session => (
                        <div key={session.id} className="px-4">
                          <SessionRow session={session} onInvalidate={handleInvalidateSession} />
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-zinc-500">
                    No active sessions
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Top Regions */}
          {usageStats?.top_regions?.length > 0 && (
            <div className="bg-zinc-950 border-x border-b border-zinc-800 rounded-b-xl p-6">
              <h3 className="text-zinc-100 font-semibold mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-zinc-400" />
                Top Regions (24h)
              </h3>
              <div className="flex flex-wrap gap-2">
                {usageStats.top_regions.map((region, i) => (
                  <div
                    key={i}
                    className="bg-zinc-900/50 border border-zinc-800 rounded-full px-3 py-1 text-sm"
                  >
                    <span className="text-zinc-300">{region.country}</span>
                    <span className="text-zinc-500 ml-1">({region.count})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bottom padding for rounded corners if no top regions */}
          {!usageStats?.top_regions?.length && (
            <div className="bg-zinc-950 border-x border-b border-zinc-800 rounded-b-xl h-4" />
          )}
        </div>
      </div>
    </div>
  );
};

export default PatreonAdminPanel;
